<template>
  <div class="w-screen min-h-screen bg-white relative pb-20">
    <div class="w-full h-full overflow-y-auto overflow-x-hidden bg-white relative">
      <div class="w-full bg-white px-4 py-3 border-b border-gray-200 flex justify-between items-center print:hidden">
        <div class="font-bold text-blue-600">E-TRANSAKSI</div>
        <svg @click="logout" xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 stroke-current text-red-400 cursor-pointer" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
        </svg>
      </div>

      <div class="p-4">
        <!-- Title -->
        <div class="text-center mb-4">
          <h5 class="font-bold text-blue-600">CATATAN BON KE VENDOR</h5>
          <h6 class="font-bold text-red-500">EF-PRINT</h6>
        </div>

        <!-- Purchase Form -->
        <div class="mb-8 p-4 border rounded-lg bg-gray-50 shadow-sm">
          <h4 class="font-bold text-sm mb-3 text-gray-700">INPUT DATA BON</h4>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <div>
              <label class="block font-semibold mb-1 text-xs">Pilih Vendor :</label>
              <div class="relative">
                <input
                  type="text"
                  v-model="searchQuery"
                  @focus="onVendorFocus"
                  @blur="hideDropdownDelayed"
                  placeholder="Ketik nama vendor..."
                  class="w-full border py-1 px-2 rounded-md text-sm"
                />
                <ul v-if="showDropdown && filteredVendors.length" class="absolute z-10 w-full bg-white border border-gray-300 mt-1 max-h-60 overflow-y-auto shadow-lg rounded-md">
                  <li
                    v-for="vendor in filteredVendors"
                    :key="vendor.id"
                    @click="selectVendor(vendor)"
                    class="px-3 py-2 hover:bg-blue-100 cursor-pointer text-sm border-b last:border-b-0"
                  >
                    {{ vendor.name }}
                  </li>
                </ul>
                <div v-if="form.vendorId && !showDropdown && selectedVendorName" class="text-xs text-green-600 mt-1">
                  Terpilih: {{ selectedVendorName }}
                </div>
              </div>
            </div>
            <div>
              <label class="block font-semibold mb-1 text-xs">Tanggal Pembelian :</label>
              <input v-model="form.date" type="date" class="w-full border py-1 px-2 rounded-md text-sm">
            </div>
            <div>
              <label class="block font-semibold mb-1 text-xs">Nama Barang :</label>
              <div class="relative">
                <input
                  type="text"
                  v-model="barangQuery"
                  @input="searchBarang"
                  @focus="onBarangFocus"
                  @blur="hideBarangDropdownDelayed"
                  placeholder="Ketik nama barang..."
                  class="w-full border py-1 px-2 rounded-md text-sm"
                />
                <ul v-if="showBarangDropdown && barangList.length" class="absolute z-10 w-full bg-white border border-gray-300 mt-1 max-h-60 overflow-y-auto shadow-lg rounded-md">
                  <li
                    v-for="brg in barangList"
                    :key="brg.id_barang"
                    @click="selectBarang(brg)"
                    class="px-3 py-2 hover:bg-blue-100 cursor-pointer text-sm border-b last:border-b-0"
                  >
                    {{ brg.nama }}
                  </li>
                </ul>
              </div>
            </div>
            <div>
              <label class="block font-semibold mb-1 text-xs">Harga Barang (Rp) :</label>
              <input v-model="form.price" type="number" class="w-full border py-1 px-2 rounded-md text-sm" placeholder="0" @focus="form.price == 0 ? form.price = '' : null" @blur="form.price === '' || form.price === null ? form.price = 0 : null">
            </div>
            <div class="flex items-center pt-5">
              <input type="checkbox" id="lunas" v-model="form.isPaid" class="mr-2 h-4 w-4">
              <label for="lunas" class="font-semibold text-xs cursor-pointer select-none">Lunas</label>
            </div>
            <div v-if="!form.isPaid">
              <label class="block font-semibold mb-1 text-xs">Jatuh Tempo :</label>
              <input v-model="form.dueDate" type="date" class="w-full border py-1 px-2 rounded-md text-sm">
            </div>
          </div>
          <div class="flex justify-end gap-2">
            <button v-if="isEdit" @click="batalEdit" class="bg-gray-500 hover:bg-gray-600 text-white py-1 px-4 rounded-md font-semibold text-xs">Batal</button>
            <button @click="addOrder" class="bg-blue-600 hover:bg-blue-700 text-white py-1 px-4 rounded-md font-semibold text-xs flex items-center gap-1">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
              </svg>
              {{ isEdit ? 'Update' : 'Simpan' }}
            </button>
          </div>
        </div>

        <h3 class="font-bold text-sm mb-2 uppercase text-red-600">TOTAL SISA BON : {{ formatRupiah(totalSisaBon) }}</h3>
        <!-- Loop through Vendors -->
        <div v-for="(vendor, index) in activeVendors" :key="vendor.id" class="mb-12">
          <h3 class="font-bold text-sm mb-2 uppercase">{{ index + 1 }}. {{ vendor.name }}</h3>

          <!-- Order Barang Table -->
          <div class="mb-4 overflow-x-auto">
            <table class="w-full border-collapse border border-gray-400 text-xs">
              <thead>
                <tr>
                  <th class="border border-gray-400 px-1 py-1 w-8 text-center">NO.</th>
                  <th class="border border-gray-400 px-1 py-1 w-20 text-center">TANGGAL</th>
                  <th class="border border-gray-400 px-1 py-1 w-20 text-center whitespace-nowrap">JATUH TEMPO</th>
                  <th class="border border-gray-400 px-1 py-1 w-20 text-center font-bold whitespace-nowrap">TGL BAYAR</th>
                  <th class="border border-gray-400 px-1 py-1 text-center font-bold whitespace-nowrap">ORDER BARANG</th>
                  <th class="border border-gray-400 px-1 py-1 w-28 text-center font-bold">JUMLAH</th>
                  <th class="border border-gray-400 px-1 py-1 w-28 text-center font-bold">BAYAR</th>
                  <th class="border border-gray-400 px-1 py-1 w-28 text-center font-bold">SISA</th>
                  <th class="border border-gray-400 px-1 py-1 w-16 text-center font-bold">AKSI</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="vendor.orders.length === 0">
                  <td colspan="9" class="border border-gray-400 px-1 py-2 text-center text-gray-500">Belum ada order</td>
                </tr>
                <tr v-for="(order, orderIndex) in paginatedOrders(vendor)" :key="orderIndex">
                  <td class="border border-gray-400 px-1 py-1 text-center">{{ getOrderNumber(vendor, orderIndex) }}</td>
                  <td class="border border-gray-400 px-1 py-1 text-center">{{ formatDate(order.date) }}</td>
                  <td class="border border-gray-400 px-1 py-1 text-center">{{ order.dueDate ? formatDate(order.dueDate) : '-' }}</td>
                  <td class="border border-gray-400 px-1 py-1 text-center">{{ order.paymentDate ? formatDate(order.paymentDate) : '-' }}</td>
                  <td class="border border-gray-400 px-1 py-1">{{ order.itemName }}</td>
                  <td class="border border-gray-400 px-1 py-1 text-right">{{ formatRupiah(order.price) }}</td>
                  <td class="border border-gray-400 px-1 py-1 text-right">{{ formatRupiah(order.paid) }}</td>
                  <td class="border border-gray-400 px-1 py-1 text-right whitespace-nowrap">
                    {{ formatRupiah(order.remaining) }}
                    <button v-if="order.remaining > 0" @click="openPaymentModal(order)" class="ml-2 bg-green-500 hover:bg-green-600 text-white text-xs px-2 py-0.5 rounded">
                      Bayar
                    </button>
                  </td>
                  <td class="border border-gray-400 px-1 py-1 text-center whitespace-nowrap">
                    <button @click="editOrder(order, vendor)" class="text-blue-600 hover:text-blue-800 mr-1" title="Edit">✏️</button>
                    <button @click="deleteOrder(order)" class="text-red-600 hover:text-red-800" title="Hapus">🗑️</button>
                  </td>
                </tr>
              </tbody>
            </table>
            <!-- Pagination -->
            <div v-if="vendor.orders.length > perPage" class="flex justify-between items-center mt-2 text-xs print:hidden">
              <span class="text-gray-500">Hal {{ getVendorPage(vendor.id) }}/{{ getTotalPages(vendor) }} ({{ vendor.orders.length }} data)</span>
              <div class="flex gap-1">
                <button @click="prevPage(vendor.id)" :disabled="getVendorPage(vendor.id) <= 1" class="px-2 py-1 border rounded disabled:opacity-30 hover:bg-gray-100">← Prev</button>
                <button @click="nextPage(vendor.id, vendor)" :disabled="getVendorPage(vendor.id) >= getTotalPages(vendor)" class="px-2 py-1 border rounded disabled:opacity-30 hover:bg-gray-100">Next →</button>
              </div>
            </div>
          </div>

           <!-- Sisa Bon Field -->
           <div class="flex justify-end mt-4">
              <div class="flex items-center gap-2 w-56">
                <label class="font-bold text-sm uppercase whitespace-nowrap">SISA BON :</label>
                <div class="border border-gray-400 h-7 flex-1 px-2 py-1 text-right font-bold text-red-600 bg-gray-100">
                  {{ formatRupiah(vendor.sisa_bon) }}
                </div>
              </div>
           </div>
        </div>
      </div>
    </div>
    <!-- Payment Modal -->
    <div v-if="showPaymentModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div class="bg-white rounded-lg p-6 w-80 shadow-lg">
        <h3 class="text-lg font-bold mb-4">Bayar Hutang</h3>
        <div class="mb-4">
          <label class="block text-sm font-semibold mb-1">Total Hutang:</label>
          <div class="text-red-500 font-bold">{{ formatRupiah(selectedPurchase.remaining) }}</div>
        </div>
        <div class="mb-4">
          <label class="block text-sm font-semibold mb-1">Jumlah Bayar:</label>
          <input
            v-model="paymentAmount"
            type="number"
            class="w-full border py-1 px-2 rounded-md"
            placeholder="Masukkan nominal"
            @input="handleInputAmount"
          >
        </div>
        <div class="mb-4">
          <label class="block text-sm font-semibold mb-1">Tanggal Bayar:</label>
          <input
            v-model="paymentDate"
            type="date"
            class="w-full border py-1 px-2 rounded-md"
          >
        </div>
        <div class="mb-4">
          <label class="block text-sm font-semibold mb-1">Sisa Hutang:</label>
          <div class="font-bold" :class="(selectedPurchase.remaining - (paymentAmount || 0)) > 0 ? 'text-red-500' : 'text-green-500'">
            {{ formatRupiah(Math.max(selectedPurchase.remaining - (paymentAmount || 0), 0)) }}
          </div>
        </div>
        <div class="flex justify-end gap-2">
          <button @click="showPaymentModal = false" class="bg-gray-300 hover:bg-gray-400 text-gray-800 py-1 px-3 rounded-md text-sm">Batal</button>
          <button @click="processPayment" class="bg-blue-600 hover:bg-blue-700 text-white py-1 px-3 rounded-md text-sm">Bayar</button>
        </div>
      </div>
    </div>

    <bottom-nav></bottom-nav>
  </div>
</template>

<script>
import BottomNav from '../components/BottomNav.vue'

export default {
  name: 'Loan', // Updated component name to match file functionality more closely if needed, but keeping original name 'Gaji' might be confusing. The file is Loan.vue. The export name was Gaji. I'll change it to Loan.
  components: { BottomNav },
  data () {
    const now = new Date()
    const offset = now.getTimezoneOffset()
    const localDate = new Date(now.getTime() - (offset * 60 * 1000))
    return {

      searchQuery: '',
      showDropdown: false,
      allVendors: [],
      selectedVendorName: '',
      barangQuery: '',
      barangList: [],
      showBarangDropdown: false,
      form: {
        vendorId: null,
        date: localDate.toISOString().slice(0, 10),
        itemName: '',
        price: 0,
        isPaid: false,
        dueDate: localDate.toISOString().slice(0, 10)
      },
      vendors: [],
      // Edit State
      isEdit: false,
      editNoTransaksi: null,
      // Payment Modal State
      showPaymentModal: false,
      selectedPurchase: null,
      paymentAmount: 0,
      paymentDate: '',
      // Pagination
      perPage: 10,
      vendorPages: {}
    }
  },
  created () {
    this.getSuppliers()
  },
  computed: {
    token () {
      return this.$store.state.token
    },
    filteredVendors () {
      if (!this.searchQuery) {
        return this.allVendors
      }
      const query = this.searchQuery.toLowerCase()
      return this.allVendors.filter(v => v.name.toLowerCase().includes(query))
    },
    activeVendors () {
      const active = this.vendors.filter(v => v.orders.length > 0 || v.payments.length > 0 || v.sisa_bon > 0)
      return active.sort((a, b) => {
        const getLatestDate = (vendor) => {
          if (vendor.orders.length === 0) return '0000-00-00'
          return vendor.orders.reduce((max, curr) => curr.date > max ? curr.date : max, '0000-00-00')
        }
        const dateA = getLatestDate(a)
        const dateB = getLatestDate(b)
        if (dateA < dateB) return 1
        if (dateA > dateB) return -1
        return 0
      })
    },
    totalSisaBon () {
      return this.activeVendors.reduce((total, vendor) => total + (parseFloat(vendor.sisa_bon) || 0), 0)
    }
  },
  methods: {
    formatRupiah (angka) {
      if (!angka) return '0'
      return new Intl.NumberFormat('id-ID').format(angka)
    },
    formatDate (dateString) {
      if (!dateString) return ''
      const [year, month, day] = dateString.split('-')
      return `${day}/${month}/${year.slice(-2)}`
    },
    getVendorPage (vendorId) {
      return this.vendorPages[vendorId] || 1
    },
    getTotalPages (vendor) {
      return Math.ceil(vendor.orders.length / this.perPage)
    },
    paginatedOrders (vendor) {
      const page = this.getVendorPage(vendor.id)
      const start = (page - 1) * this.perPage
      return vendor.orders.slice(start, start + this.perPage)
    },
    getOrderNumber (vendor, orderIndex) {
      const page = this.getVendorPage(vendor.id)
      return (page - 1) * this.perPage + orderIndex + 1
    },
    prevPage (vendorId) {
      const current = this.getVendorPage(vendorId)
      if (current > 1) {
        this.vendorPages[vendorId] = current - 1
      }
    },
    nextPage (vendorId, vendor) {
      const current = this.getVendorPage(vendorId)
      if (current < this.getTotalPages(vendor)) {
        this.vendorPages[vendorId] = current + 1
      }
    },
    getSuppliers () {
      this.axios.get('e_transaksi/transaksi/get_active_suppliers?limit=10&token=' + this.token).then(response => {
        if (response.data.status) {
          this.vendors = response.data.data.map(supplier => ({
            id: supplier.id_supplier,
            name: supplier.nama,
            orders: [],
            payments: [],
            sisa_bon: 0 // Will be calculated from orders
          }))
          this.getPurchases() // Fetch purchases after suppliers are set
        }
      }).catch(error => {
        console.error('Error fetching suppliers:', error)
      })
    },
    getPurchases () {
      this.axios.get('e_transaksi/transaksi/get_all_purchases?token=' + this.token).then(response => {
        if (response.data.status) {
          // Reset sisa_bon first
          this.vendors.forEach(v => { v.sisa_bon = 0 })

          response.data.data.forEach(purchase => {
            const vendor = this.vendors.find(v => v.id === purchase.supplier_id)
            if (vendor) {
              vendor.orders.push({
                date: purchase.tgl_transaksi.split(' ')[0], // Take only date part
                dueDate: purchase.jatuh_tempo ? purchase.jatuh_tempo.split(' ')[0] : null,
                itemName: `${purchase.nama_barang} (${purchase.jumlah}x)`,
                price: parseFloat(purchase.total_harga),
                paid: parseFloat(purchase.bayar || 0),
                remaining: parseFloat(purchase.sisa || 0),
                no_transaksi: purchase.no_transaksi,
                paymentDate: purchase.tgl_pembayaran ? purchase.tgl_pembayaran.split(' ')[0] : null
              })
              vendor.sisa_bon += parseFloat(purchase.sisa || 0)
            }
          })
        }
      }).catch(error => {
        console.error('Error fetching purchases:', error)
      })
    },
    onVendorFocus () {
      this.showDropdown = true
      if (this.allVendors.length === 0) {
        this.axios.get('e_transaksi/transaksi/get_suppliers?token=' + this.token).then(response => {
          if (response.data.status) {
            this.allVendors = response.data.data.map(s => ({
              id: s.id_supplier,
              name: s.nama
            }))
          }
        })
      }
    },
    selectVendor (vendor) {
      this.form.vendorId = vendor.id
      this.selectedVendorName = vendor.name
      this.searchQuery = vendor.name
      this.showDropdown = false
    },
    hideDropdownDelayed () {
      setTimeout(() => {
        this.showDropdown = false
      }, 200)
    },
    onBarangFocus () {
      this.showBarangDropdown = true
      if (this.barangList.length === 0) {
        this.axios.get('e_transaksi/transaksi/get_barang?token=' + this.token + '&q=' + (this.barangQuery || '')).then(response => {
          if (response.data.status) {
            this.barangList = response.data.data
          }
        })
      }
    },
    searchBarang () {
      this.form.itemName = this.barangQuery
      this.axios.get('e_transaksi/transaksi/get_barang?token=' + this.token + '&q=' + this.barangQuery).then(response => {
        if (response.data.status) {
          this.barangList = response.data.data
          this.showBarangDropdown = true
        }
      })
    },
    selectBarang (brg) {
      this.form.itemName = brg.nama
      this.barangQuery = brg.nama
      if (brg.harga_beli && parseFloat(brg.harga_beli) > 0) {
        this.form.price = parseFloat(brg.harga_beli)
      }
      this.showBarangDropdown = false
      this.barangList = []
    },
    hideBarangDropdownDelayed () {
      setTimeout(() => {
        this.showBarangDropdown = false
      }, 200)
    },
    addOrder () {
      if (!this.form.vendorId) {
        alert('Mohon pilih vendor terlebih dahulu')
        return
      }
      if (!this.form.itemName || this.form.price <= 0) {
        alert('Mohon isi nama barang dan harga dengan benar')
        return
      }

      const formData = new FormData()
      formData.append('token', this.token)
      formData.append('supplier_id', this.form.vendorId)
      formData.append('tgl_transaksi', this.form.date)
      formData.append('nama_barang', this.form.itemName)
      formData.append('harga', this.form.price)
      formData.append('is_paid', this.form.isPaid ? 1 : 0)
      if (!this.form.isPaid && this.form.dueDate) {
        formData.append('jatuh_tempo', this.form.dueDate)
      }

      let url = 'e_transaksi/transaksi/store_purchase'
      if (this.isEdit) {
        url = 'e_transaksi/transaksi/update_purchase'
        formData.append('no_transaksi', this.editNoTransaksi)
      }

      this.axios.post(url, formData).then(response => {
        if (response.data.status) {
          alert(this.isEdit ? 'Data berhasil diupdate' : 'Data berhasil disimpan')
          // Reset form
          this.form.itemName = ''
          this.form.price = 0
          this.form.isPaid = false
          this.form.dueDate = this.form.date
          this.isEdit = false
          this.editNoTransaksi = null
          this.barangQuery = ''

          // Refresh Data
          this.getSuppliers()
        } else {
          alert('Gagal menyimpan: ' + response.data.message)
        }
      }).catch(error => {
        console.error('Error storing purchase:', error)
        alert('Terjadi kesalahan saat menyimpan data')
      })
    },
    editOrder (order, vendor) {
      this.isEdit = true
      this.editNoTransaksi = order.no_transaksi
      this.form.vendorId = vendor.id
      this.selectedVendorName = vendor.name
      this.searchQuery = vendor.name
      this.form.date = order.date
      // Extract item name without quantity suffix e.g. "Kertas A4 (1x)" -> "Kertas A4"
      const itemName = order.itemName.replace(/\s*\(\d+x\)$/, '')
      this.form.itemName = itemName
      this.barangQuery = itemName
      this.form.price = order.price
      this.form.isPaid = order.remaining <= 0
      this.form.dueDate = order.dueDate || this.form.date
      // Scroll to form
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    deleteOrder (order) {
      if (!confirm('Anda yakin ingin menghapus data ini?')) return

      const formData = new FormData()
      formData.append('token', this.token)
      formData.append('no_transaksi', order.no_transaksi)

      this.axios.post('e_transaksi/transaksi/delete_purchase', formData).then(response => {
        if (response.data.status) {
          alert('Data berhasil dihapus')
          this.getSuppliers()
        } else {
          alert('Gagal menghapus: ' + response.data.message)
        }
      }).catch(error => {
        console.error('Error deleting purchase:', error)
        alert('Terjadi kesalahan saat menghapus data')
      })
    },
    batalEdit () {
      this.isEdit = false
      this.editNoTransaksi = null
      this.form.itemName = ''
      this.form.price = 0
      this.form.isPaid = false
      this.searchQuery = ''
      this.barangQuery = ''
      this.selectedVendorName = ''
      this.form.vendorId = null
    },
    openPaymentModal (order) {
      this.selectedPurchase = order
      this.paymentAmount = order.remaining
      this.paymentDate = new Date().toISOString().slice(0, 10)
      this.showPaymentModal = true
    },
    handleInputAmount (e) {
      // Remove leading zeros visually
      const val = e.target.value
      // Check if value starts with 0 and has more than 1 char, and second char is not .
      if (val.length > 1 && val.startsWith('0') && val[1] !== '.') {
        const newVal = parseInt(val)
        this.paymentAmount = newVal

        // Force update to ensure DOM reflects state
        this.$nextTick(() => {
          e.target.value = newVal
        })
      }
    },
    processPayment () {
      if (!this.paymentAmount || this.paymentAmount <= 0) {
        alert('Mohon masukkan jumlah pembayaran yang valid')
        return
      }
      if (this.paymentAmount > this.selectedPurchase.remaining) {
        alert('Jumlah pembayaran melebihi sisa hutang')
        return
      }

      const formData = new FormData()
      formData.append('token', this.token)
      formData.append('no_transaksi', this.selectedPurchase.no_transaksi) // Use no_transaksi
      formData.append('nominal', this.paymentAmount)
      formData.append('tanggal', this.paymentDate)

      this.axios.post('e_transaksi/transaksi/pay_purchase', formData).then(response => {
        if (response.data.status) {
          alert('Pembayaran berhasil')
          this.showPaymentModal = false
          this.selectedPurchase = null
          this.paymentAmount = 0
          this.getSuppliers() // Refresh data
        } else {
          alert('Gagal: ' + response.data.message)
        }
      }).catch(error => {
        console.error('Error processing payment:', error)
        alert('Terjadi kesalahan saat memproses pembayaran')
      })
    },
    logout () {
      localStorage.removeItem('X-FPRINT-TOKEN')
      localStorage.removeItem('X-FPRINT-USER')
      this.$store.dispatch('set_token', null)
      this.$store.dispatch('set_user', null)
      this.$router.push({ name: 'Login' })
    }
  }
}
</script>
